import { Quill } from 'react-quill';

import { Base64 } from 'js-base64';


// Custom button icons
const icons = Quill.import('ui/icons');
icons['textColor'] = '<i class="fa-solid fa-palette" style="font-size: 14px;"></i>';

export const rgbStringToObject = (rgbString: string): { r: number, g: number, b: number } => {
    const match = rgbString.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
    if (match) {
        return {
            r: parseInt(match[1]),
            g: parseInt(match[2]),
            b: parseInt(match[3])
        };
    }
    return { r: 0, g: 0, b: 0 };
};

export const rgbObjectToString = (rgb: { r: number, g: number, b: number }): string => {
    return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
};

export const formats = [
    'size',
    'header',
    'bold',
    'italic',
    'underline',
    'color',
    'link'
];

/**
 * Checks if rich text content is effectively empty
 * Returns true for content like "<p><br></p>", "<p></p>", empty strings, etc.
 */
export const isRichTextEmpty = (text?: string): boolean => {
    if (!text || text.trim() === '') {
        return true;
    }

    const textContent = text
        .replace(/<br\s*\/?>/gi, '') // Remove <br> tags
        .replace(/<[^>]*>/g, '') // Remove all other HTML tags
        .replace(/&nbsp;/g, ' ') // Replace &nbsp; with spaces
        .trim();

    return textContent === '';
};

/**
 * Encodes rich text content to base64 for API storage
 * Also extracts and cleans color information
 */
export const processRichTextContent = (richText?: { text?: string; fontColor?: string }) => {
    const content = richText?.text || '';
    const isEmpty = isRichTextEmpty(content);

    // If content is empty, always use black color
    if (isEmpty) {
        return {
            text: Base64.encode(content),
            fontColor: 'rgb(0, 0, 0)'
        };
    }

    // Extract color from HTML content (keep as RGB)
    const colorMatch = content.match(/color:\s*([^;"]+)/);
    const extractedColor = colorMatch ? colorMatch[1].trim() : (richText?.fontColor || 'rgb(0, 0, 0)');

    // Clean HTML by removing all color styles
    const cleanText = content.replace(/style="[^"]*"/g, (match) => {
        const cleanedStyle = match.replace(/color:[^;"]*;?/g, '').replace(/;\s*;/g, ';').replace(/style="[;\s]*"/g, '');
        return cleanedStyle === 'style=""' ? '' : cleanedStyle;
    });

    const encodedText = Base64.encode(cleanText);

    return {
        text: encodedText,
        fontColor: extractedColor
    };
};

/**
 * Decodes rich text content from base64 for editing
 * Returns decoded text or empty string if decoding fails
 */
export const decodeRichTextContent = (richText?: { text?: string; fontColor?: string }) => {
    if (!richText?.text) {
        return {
            text: '',
            fontColor: richText?.fontColor || 'rgb(0, 0, 0)'
        };
    }

    try {
        return {
            text: Base64.decode(richText.text),
            fontColor: richText.fontColor || 'rgb(0, 0, 0)'
        };
    } catch (error) {
        // If decoding fails, assume it's already decoded
        return {
            text: richText.text,
            fontColor: richText.fontColor || 'rgb(0, 0, 0)'
        };
    }
};

export const extractColorValue = (color: any): string => {
    if (typeof color === 'string') {
        return color;
    }
    if (color && typeof color === 'object' && color.colorOne) {
        return color.colorOne;
    }
    return '';
};
